import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { STATUS_STYLES, label } from "@/lib/format";

export function PageHeader({ eyebrow, title, subtitle, children }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-7">
      <div>
        {eyebrow && <div className="eyebrow mb-2">{eyebrow}</div>}
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C]" data-testid="page-title">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1.5 max-w-2xl">{subtitle}</p>}
      </div>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}

export function SelectBox({ value, onChange, options, placeholder, testid, className = "w-44", allLabel }) {
  return (
    <Select value={value || "__all"} onValueChange={(v) => onChange(v === "__all" ? "" : v)}>
      <SelectTrigger className={`${className} bg-white`} data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {allLabel && <SelectItem value="__all">{allLabel}</SelectItem>}
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function MarketSelect({ value, onChange, testid = "market-filter" }) {
  const { meta } = useAuth();
  return <SelectBox value={value} onChange={onChange} testid={testid} allLabel="All markets" options={meta.markets.map((m) => ({ value: m.key, label: m.name }))} />;
}

export function PeopleSelect({ value, onChange, testid = "salesperson-filter", allLabel = "All salespeople", includeUnassigned }) {
  const { people } = useAuth();
  const opts = people.filter((p) => p.assignable).map((p) => ({ value: p.id, label: p.name }));
  if (includeUnassigned) opts.unshift({ value: "unassigned", label: "Unassigned" });
  return <SelectBox value={value} onChange={onChange} testid={testid} allLabel={allLabel} options={opts} />;
}

export function StatusBadge({ status, testid }) {
  return <Badge variant="outline" className={`font-medium ${STATUS_STYLES[status] || ""}`} data-testid={testid}>{label(status)}</Badge>;
}

export function DemoTag({ show }) {
  if (!show) return null;
  return <span className="ml-1.5 inline-block text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 align-middle">DEMO</span>;
}

export function Empty({ children, testid }) {
  return <div className="text-sm text-slate-400 py-10 text-center border border-dashed rounded bg-white" data-testid={testid}>{children}</div>;
}

export function Panel({ title, action, children, className = "", testid }) {
  return (
    <section className={`bg-white border border-slate-200 rounded-md ${className}`} data-testid={testid}>
      {title && (
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-slate-100">
          <h3 className="font-semibold text-[15px] text-[#0B192C]">{title}</h3>
          {action}
        </div>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}
